import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { triggerHaptic } from '../../../../utils/telegramActions';
import './TextArea.scss';

const TextArea = forwardRef(({
    maxLength = 120,
    minLength, // Опциональный пропс для минимальной длины
    headerText = "Дорогой КІТ Мороз! В 2026 году\nя хочу, чтобы мой бизнес…",
    placeholderText = "Например: вышел на оборот 100\u00A0млн.",
    value: controlledValue,
    onChange: controlledOnChange,
    onFocus,
    onBlur,
    onKeyDown, // Новый проп для обработки нажатий клавиш
    isSmallHeight = false, // Новый пропс для уменьшенной высоты
    enterKeyHint = "done" // Тип кнопки на клавиатуре: "next", "done", "go", "search", "send"
}, ref) => {
    const [internalValue, setInternalValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const textareaRef = useRef(null);
    const isControlled = controlledValue !== undefined;
    const value = isControlled ? controlledValue : internalValue;

    // Экспонируем методы focus и blur для родительского компонента
    useImperativeHandle(ref, () => ({
        focus: () => {
            textareaRef.current?.focus();
        },
        blur: () => {
            textareaRef.current?.blur();
        }
    }));

    // Красная обводка только при достижении максимума символов и наличии фокуса
    const isInvalid = isFocused && value.length === maxLength;

    const handleChange = (e) => {
        let newValue = e.target.value;

        // Force first letter capitalization
        if (newValue.length > 0) {
            newValue = newValue.charAt(0).toUpperCase() + newValue.slice(1);
        }

        if (newValue.length <= maxLength) {
            if (isControlled) {
                controlledOnChange?.(newValue);
            } else {
                setInternalValue(newValue);
            }
        }
    };

    const handleFocus = () => {
        triggerHaptic('light');
    };

    const handleFocusInternal = (e) => {
        setIsFocused(true);
        handleFocus(); // Call the new haptic feedback handler
        if (onFocus) onFocus(e);
    };

    const handleBlurInternal = (e) => {
        setIsFocused(false);
        if (onBlur) onBlur(e);
    };

    // Авто-ресайз высоты
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, [value]);

    const formatHeaderText = (text) => {
        // Заменяем переносы строк на пробелы
        let formatted = text.replace(/\n/g, ' ');
        
        // Заменяем пробел между цифрами и следующими словами на неразрывный пробел
        // Например: "2026 году" -> "2026\u00A0году", "100 млн" -> "100\u00A0млн"
        // Используем [ \t] вместо \s, чтобы не трогать уже существующие неразрывные пробелы
        formatted = formatted.replace(/(\d+)[ \t]+([а-яёА-ЯЁ]+)/gi, '$1\u00A0$2');
        
        // Заменяем пробел между "я" и "хочу" на неразрывный пробел, чтобы они всегда были на одной строке
        formatted = formatted.replace(/(я|Я)\s+(хочу|Хочу)/gi, '$1\u00A0$2');
        
        // Оборачиваем символ бета (β или ᵝ) в span с классом для другого шрифта
        // β - греческая буква бета (U+03B2)
        // ᵝ - модификатор буквы бета (U+1D5D)
        formatted = formatted.replace(/([βᵝ])/g, '<span class="beta-symbol">$1</span>');
        
        return formatted;
    };

    const formatPlaceholderText = (text) => {
        // Заменяем пробел между цифрами и следующими словами на неразрывный пробел
        // Например: "100 млн" -> "100\u00A0млн"
        // Используем [ \t] вместо \s, чтобы не трогать уже существующие неразрывные пробелы
        return text.replace(/(\d+)[ \t]+([а-яёА-ЯЁ]+)/gi, '$1\u00A0$2');
    };

    return (
        <div className="text-area-wrapper">
            <div 
                className="text-area-header-text"
                dangerouslySetInnerHTML={{ __html: formatHeaderText(headerText) }}
            />
            <div className={`text-area-container ${isInvalid ? 'invalid' : ''}`}>
                <div className="text-area-inner">
                    <div className={`text-area-content ${isSmallHeight ? 'small' : ''}`}>
                        <textarea
                            ref={textareaRef}
                            className={`text-area-input ${isSmallHeight ? 'small' : ''}`}
                            value={value}
                            onChange={handleChange}
                            onFocus={handleFocusInternal}
                            onBlur={handleBlurInternal}
                            onKeyDown={onKeyDown}
                            maxLength={maxLength}
                            placeholder=""
                            spellCheck={false}
                            autoCorrect="off"
                            autoCapitalize="sentences"
                            autoComplete="off"
                            enterKeyHint={enterKeyHint}
                        />
                        {!value && (
                            <div className={`text-area-placeholder ${isSmallHeight ? 'small' : ''}`}>
                                {formatPlaceholderText(placeholderText)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className={`text-area-counter ${isInvalid ? 'invalid' : ''}`}>
                {value.length}/{maxLength}
            </div>
        </div>
    );
});

TextArea.displayName = 'TextArea';

export default TextArea;

